import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {Tabs, Layout, Row, Col, Typography, Card, Divider} from 'antd';
import { MongoQueryBuilder } from "../../utils/mongoQuery";
import { fetchFilteredTickers, fetchTickerData } from "../../api/tickersApi";
import { fetchDetailedPortfolioObjects } from '../../api/portfolioApi';
import { fetchTimeSeriesData } from '../../api/timeseriesApi';
import { selectTimeSeriesData, selectManyTimeSeriesData } from '../../store/slices/timeseries.slice';
import MaxExposure from "./MaxExposure/MaxExposure";
import PortfolioAllocations from "../PortfolioAllocations/PortfolioAllocations";
import PerformanceComparison from "../PerformanceComparison/PerformanceComaprison";
import PageHeader  from "../../layouts/PageHeader";
import { formatCompanyLogoUrl } from "../../api/companyApi";
import { toDate } from "../../utils/datetime";
import Overview from "./views/Overview";
import TickersView from "./views/TickersView";
import SectorIndustryChart from "../../components/Charts/SectorIndustryChart";
import Disclaimer from "../../components/Disclaimer/Disclaimer";
import ResponsiveImageContainer from "../../components/ResponsiveImageContainer";
import ShareView from "./views/ShareView";
import {useTickerSearchIndex} from "../../modules/Search/useTickerSearchIndex";
import TickerChip from "../../components/TickerChip";
import TreeView from "./views/TreeView";
import CompositionView from "./views/CompositionView";
import StatsView from "./views/StatsView";
const { TabPane } = Tabs;
const { Content, Footer } = Layout;


const ALL_VIEWS = [
    'overview', 'tickers', 'details', 'max exposure', 'allocations', 'performance', 'share'
]


const formatMongoQuery = (symbols)=>{
    const query = new MongoQueryBuilder();
    query.inList('symbol', symbols);
    query.updateProjection(['symbol', 'name', 'type', 'tags', 'stats.benchmarks.spy.corr_1_yr', 'logo']);
    return query.toMongoQuery();
}

const formatInitialComparisonObjects = (portfolio)=>{
    const items = [
        {id: portfolio.id, label: portfolio.name, benchmark: portfolio.benchmark},
        {id: portfolio.benchmark, label: portfolio.benchmark, benchmark: portfolio.benchmark}
    ]
    const viewConfig = portfolio?.view_config?.comparison ?? {};
    const initialTickerSymbols = viewConfig.ticker_ids || [];
    const initialPortfolioIds = viewConfig.portfolio_ids || [];
    if (initialTickerSymbols.length){
        items.push(...initialTickerSymbols.map(symbol=>({id: symbol, label: symbol, benchmark: portfolio.benchmark})));
    }
    if (initialPortfolioIds.length){
        items.push(...initialPortfolioIds.map(id=>({id, label: id, benchmark: portfolio.benchmark})));
    }
    return items;

}



const PortfolioView = () => {
    const { portfolioId } = useParams(); // This retrieves the dynamic part of the URL
    const [searchParams]  = useSearchParams();
    const location = useLocation();
    const dispatch = useDispatch();
    const portfolio = useSelector(state => state.portfolios.items.find(p => p.id === portfolioId));
    const [timeSeriesSymbols, setTimeSeriesSymbols] = useState([portfolioId, portfolio?.benchmark]);
    const [notExtraSymbols] = useState([portfolioId, portfolio?.benchmark, 'custodial_cash']);  // Symbols to exclude from remainder
    const portfolioTimeseries = useSelector(state=>selectTimeSeriesData(state, [portfolioId]));
    const benchmarkTimeseries = useSelector(state=>selectTimeSeriesData(state, [portfolio?.benchmark]));
    const extraTimeseries = useSelector(state=>selectManyTimeSeriesData(state, timeSeriesSymbols.filter(symbol=>!notExtraSymbols.includes(symbol))));
    const rawTickers = useSelector(state => state.tickers.filteredTickers); // Accessing tickers from the Redux store
    const [loadingDetailedPortfolio, setLoadingDetailedPortfolio] = useState(false);
    const viewsFromParams = searchParams?.get?.('views')?.split?.(',');
    const { tickers } = useTickerSearchIndex([portfolio?.benchmark]);
    const benchmarkTicker = tickers?.[0];
    const benchmarkCompositeData = useSelector(state => state.tickers.tickers?.[portfolio?.benchmark]);

    const allowScreenshot = !location.pathname.startsWith('/public');
    const [activeTab, setActiveTab] = useState('1');
    const hasTree = portfolio?.tree && Object.keys(portfolio.tree).length > 0;




    const tabIsDisabled = (tab)=>{
        if (!searchParams || !viewsFromParams) return false;
        return !viewsFromParams.includes(tab);
    }

    const getDefaultTabIndex = ()=>{
        if (!searchParams || !viewsFromParams) return "1";
        return (ALL_VIEWS.indexOf(viewsFromParams[0]) + 1).toString();
    }

    const addTimeseriesSymbols = useCallback((newSymbols)=>{
        setTimeSeriesSymbols(prevSymbols => {
            const allSymbols = new Set([...prevSymbols, ...newSymbols]);
            return Array.from(allSymbols);
        });
    }, [setTimeSeriesSymbols]);
    const removeTimeseriesSymbols = useCallback((symbols)=>{
        setTimeSeriesSymbols(prevSymbols => {
            const allSymbols = new Set(prevSymbols);
            symbols.forEach(symbol => allSymbols.delete(symbol));
            return Array.from(allSymbols);
        });
    })


    const fetchTimeSeries = useCallback(async () => {
        if (!loadingDetailedPortfolio && portfolio) {
            let commonStart = toDate(portfolio.inception_date);
            const currentTimeseriesSymbols = new Set([benchmarkTimeseries, ...extraTimeseries].map(series => series?.symbol));
            currentTimeseriesSymbols.add(portfolioId);
            const symbolsToFetch = timeSeriesSymbols.filter(symbol => !currentTimeseriesSymbols.has(symbol));
            if (!portfolioTimeseries){
                symbolsToFetch.push(portfolioId);
            }
            await dispatch(fetchTimeSeriesData({ symbols: symbolsToFetch, startDate: commonStart }));
        }
    }, [dispatch, loadingDetailedPortfolio, timeSeriesSymbols, portfolio]);


    // Effect to fetch the detailed portfolio if not already loaded
    useEffect(() => {
        const loadDetailedPortfolio = async () => {
            if (!portfolioId) return;
            setLoadingDetailedPortfolio(true);
            dispatch(fetchDetailedPortfolioObjects([portfolioId])).then((response)=>{
                setLoadingDetailedPortfolio(false);
                if (timeSeriesSymbols.length===2 && !timeSeriesSymbols[1]){
                    setTimeSeriesSymbols([portfolioId, response.payload[0].benchmark]);
                }
            })
        };
        loadDetailedPortfolio();
    }, [portfolioId, dispatch]);

    // Effect to fetch the ticker details for TickerTable etc..
    useEffect(() => {

        if (!loadingDetailedPortfolio && portfolio?.symbols) {
            const query = formatMongoQuery(Object.keys(portfolio.symbols));
            dispatch(fetchFilteredTickers(query));
        }
    }, [dispatch, loadingDetailedPortfolio]);
    useEffect(() => {

        if (!loadingDetailedPortfolio && portfolio?.benchmark) {

            dispatch(fetchTickerData({symbols: [portfolio?.benchmark], fields: ['composite', 'name']}));
        }
    }, [dispatch, loadingDetailedPortfolio]);

   useEffect(() => {
        if (!loadingDetailedPortfolio && portfolio?.hasOwnProperty('benchmark')) {
            fetchTimeSeries();
        }
    }, [fetchTimeSeries, loadingDetailedPortfolio, portfolio]);

  // Filters tickers to include only those currently with a weight > 0
  const enhancedTickers = useMemo(() => {
      if (!portfolio?.symbols || !rawTickers.length) return [];
      const portfolioTickers = rawTickers.filter(ticker => Object.keys(portfolio.symbols).includes(ticker.symbol));
      return portfolioTickers.map(ticker => {
          const weight = portfolio.symbols[ticker.symbol] * 100; // Extract tickers weight within portfolio
          return { ...ticker, weight };
      });
    }, [portfolio, rawTickers]);

  const isDataReady = Array.isArray(portfolioTimeseries) && portfolioTimeseries.length > 1;

  let extraItems;
  if (portfolio && portfolio.hasOwnProperty('symbols') && portfolio.symbols){
      extraItems = Object.keys(portfolio.symbols).map((symbol, index) => {
          return {id: symbol, label: symbol};
      });
  }


  const actions = [];
  if (portfolio && portfolio.hasOwnProperty('id')) {
      actions.push(...[
          <Typography.Title level={5}>
              Equities {portfolio.targets?.equity} / Bonds {100-portfolio.targets?.equity}
          </Typography.Title>,
          benchmarkTicker && <TickerChip ticker={benchmarkTicker} />,
          <Typography.Title level={4} style={{fontWeight: '400', color: '#000', fontFamily: "var(--font-main)"}}>Professional Use Only</Typography.Title>
      ]);
  }

  return (
      <Layout style={{display: 'flex', flexDirection: 'column', height: '100vh'}}>
          <Helmet>
              <link rel={'icon'} href={'https://storage.googleapis.com/finiat-web-static-resources/company-brands/portfolio-delta.ico'} />
              <title>{portfolio?.name}</title>
          </Helmet>
          <PageHeader
              title={portfolio?.name}
              logoUrl={formatCompanyLogoUrl()}
              logoSize={'60px'}
              actions={actions}
          />
          <Content style={{flex: 1, overflowY: 'auto', background: "#FFF"}}>
              <div>
                  <Tabs onChange={(key)=>setActiveTab(key)} defaultActiveKey={getDefaultTabIndex()} centered={true}>
                      <TabPane tab={'Overview (New)'} key={'1'} >
                          <Overview portfolio={portfolio}
                                    portfolioTimeSeries={portfolioTimeseries}
                                    benchmarkTimeSeries={benchmarkTimeseries}
                                    isDataReady={isDataReady}
                                    addTimeseriesSymbols={addTimeseriesSymbols}
                                    removeTimeseriesSymbols={removeTimeseriesSymbols}
                                    extraTimeseries={extraTimeseries}
                                    allTickers={rawTickers}

                          />
                      </TabPane>
                      <TabPane tab="Tickers" key="2" disabled={tabIsDisabled('tickers')}>
                          <TickersView {...{portfolio, enhancedTickers}} padding={'p-5'} />
                      </TabPane>
                      <TabPane tab="Details" key="3" disabled={tabIsDisabled('details')}>
                          {portfolio && <CompositionView portfolio={portfolio} benchmark={benchmarkCompositeData} />}
                      </TabPane>
                      <TabPane tab="Max Exposure" key="4" disabled={tabIsDisabled('max exposure')}>
                          <div className={'p-5'}>
                              <MaxExposure treeData={portfolio?.tree} symbols={portfolio?.symbols} showOrient={activeTab==='4'} />
                          </div>
                      </TabPane>
                      {portfolio && (
                          <TabPane tab="Allocations Over Time" key="5" disabled={tabIsDisabled('allocations')}>
                              <div className={'p-2'}>
                                  <PortfolioAllocations pid={portfolioId} showOrient={activeTab==='5'} />
                              </div>
                          </TabPane>
                      )}
                      {portfolio && (
                          <TabPane tab="Comparison" key="6" disabled={tabIsDisabled('performance')}>
                            <PerformanceComparison
                                  initialObjects={formatInitialComparisonObjects(portfolio)}
                                  isChild={true}
                                  extraItems={extraItems}
                                  allowScreenshot={allowScreenshot}
                            />
                          </TabPane>
                      )}
                      <TabPane tab={'Share'} key={'8'} disabled={tabIsDisabled('share')} >
                          <ResponsiveImageContainer
                            src={'https://storage.googleapis.com/finiat-web-static-resources/library/graphic-photo/white-cityscapes/3d-city-from-side.jpeg'}
                            children={
                              <ShareView portfolioId={portfolioId} />
                            }
                            height={'60%'}
                            width={'100%'}
                          />
                      </TabPane>
                      {hasTree && (
                          <TabPane tab={'Tree'} key={'c-1'}>
                              <TreeView tree={portfolio.tree} />
                          </TabPane>
                      )}
                      <TabPane tab={'Stats'} key={'c-3'}>
                          {portfolio &&  <StatsView portfolioId={portfolio.id} benchmark={portfolio.benchmark}/> }
                      </TabPane>
                  </Tabs>
              </div>
          </Content>
          <Footer className={'my-6'} style={{background: '#fff', height: '20px'}}>
              <Disclaimer />
          </Footer>

      </Layout>
  );
};


// <TabPane tab={'Attribution'} key={'c-2'}>
//     {portfolio && portfolio.hasOwnProperty('symbols') && (
//         <AttributionModel tickerSymbol={portfolio.id} />
//     )}
// </TabPane>

export default PortfolioView;